import { format, parse, isValid } from "date-fns";

const DATE_FALLBACKS_DATE = [
  "yyyy-MM-dd",        // 2012-12-31
  "MM/dd/yyyy",        // 12/31/2012
  "dd/MM/yyyy",        // 31/12/2012
  "MMM dd, yyyy",      // Dec 31, 2015
  "MMMM d, yyyy",      // December 31, 2015
  "MMMM d yyyy",      // December 31, 2015
  "MMMM dd yyyy",      // December 31, 2015
  "MMMM dd, yyyy",     // December 01, 2015
  "dd MMM yyyy",       // 31 Dec 2015
  "MMM dd yyyy",       // Dec 31 2015
];

const DATE_FALLBACKS_DATETIME = [
  "yyyy-MM-dd'T'HH:mm:ssXXX", // 2012-12-31T15:04:05-05:00
  "yyyy-MM-dd'T'HH:mm:ss",    // 2012-12-31T15:04:05
  "MM/dd/yyyy h:mm aa",       // 12/31/2012 3:04 PM
  "MMM d, yyyy h:mm aa",      // Dec 31, 2015 3:04 PM
  "MMMM d, yyyy h:mm aa",     // December 31, 2015 3:04 PM
  "MMMM dd, yyyy h:mm aa",     // December 31, 2015 3:04 PM
  "MMMM d yyyy h:mm aa",     // December 31, 2015 3:04 PM
  "MMMM dd yyyy h:mm aa",     // December 31, 2015 3:04 PM
];

type HasDateConfig = {
  showTimeSelect?: boolean;
  dateConfig?: {
    displayFormatDate?: string;
    displayFormatDateTime?: string;
    storageFormatDate?: string;
    storageFormatDateTime?: string;
  };
};

export function isDateTime(field: HasDateConfig) {
  return !!field.showTimeSelect;
}

export function getDisplayFormat(field: HasDateConfig) {
  const d = field.dateConfig || {};
  return isDateTime(field)
    ? d.displayFormatDateTime || "MM/dd/yyyy h:mm aa"
    : d.displayFormatDate || "MM/dd/yyyy";
}

export function getStorageFormat(field: HasDateConfig) {
  const d = field.dateConfig || {};
  return isDateTime(field)
    ? d.storageFormatDateTime || "yyyy-MM-dd'T'HH:mm:ssXXX"
    : d.storageFormatDate || "yyyy-MM-dd";
}

function looksIsoWithOffset(s: string) {
  return /[+-]\d{2}:\d{2}$|Z$/i.test(s);
}

function tryParseWithFormats(raw: string, formats: string[]) {
  for (const f of formats) {
    const d = parse(raw, f, new Date());
    if (isValid(d)) return d;
  }
  // Last resort: Date constructor for broadly compatible strings
  const d2 = new Date(raw);
  return isValid(d2) ? d2 : null;
}

export function toPickerDate(field: HasDateConfig, stored?: string | null): Date | null {
  if (!stored) return null;
  const storageFmt = getStorageFormat(field);

  if (isDateTime(field)) {
    if (looksIsoWithOffset(stored)) {
      const d = new Date(stored);
      return isValid(d) ? d : null;
    }
    // First try the declared storage format, then fallbacks
    const first = parse(stored, storageFmt, new Date());
    if (isValid(first)) return first;
    const alt = tryParseWithFormats(stored, DATE_FALLBACKS_DATETIME);
    return alt;
  } else {
    // Date-only: try storage format then fallbacks
    const first = parse(stored, storageFmt, new Date());
    let d = isValid(first) ? first : tryParseWithFormats(stored, DATE_FALLBACKS_DATE);
    if (!d) return null;
    // Normalize to midnight local for date-only
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  }
}

export function fromPickerDate(field: HasDateConfig, picked: Date | null): string | null {
  if (!picked) return null;
  const fmt = getStorageFormat(field);
  return format(picked, fmt);
}

export const normalizeStoredDateForDisplay = (
  stored: string,
  displayFormat?: string
) => {
  if (!displayFormat) return stored;

  if (displayFormat.includes("dd")) {
    return stored;
  }

  if (displayFormat.includes("d")) {
    return stored.replace(
      /^(\d{4}-\d{2})-0(\d)$/,
      "$1-$2"
    );
  }

  return stored;
}
